import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, ExternalLink, Image, Upload, X } from 'lucide-react';
import { CLINIC_ID } from './supabase.js';
import { addVisitPhoto, loadVisitPhotos } from './data.js';
import { uploadImageFile } from './imageStorage.js';
import { dateLabel, friendlyError } from './lib/format.js';

const PHOTO_TYPES = [
  { value: 'BEFORE',  label: 'Sebelum Tindakan' },
  { value: 'AFTER',   label: 'Sesudah Tindakan' },
  { value: 'CONSENT', label: 'Informed Consent' },
];

const DENTAL_PHOTO_TYPES = [
  ...PHOTO_TYPES,
  { value: 'XRAY', label: 'Citra XRay' },
];

/** Kompres gambar ke max 1920px, JPEG 85% */
async function compressImage(file, maxPx = 1920, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const ratio = Math.min(maxPx / img.width, maxPx / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        resolve(blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }) : file);
      }, 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export function PhotoModal({ visit, visitId, client, context = 'general', onClose }) {
  const activeVisitId = visit?.id || visitId;
  const photoTypes = context === 'dental' ? DENTAL_PHOTO_TYPES : PHOTO_TYPES;
  const [tab, setTab]             = useState('upload');
  const [photoType, setPhotoType] = useState('BEFORE');
  const [notes, setNotes]         = useState('');
  const [files, setFiles]         = useState([]);
  const [previews, setPreviews]   = useState([]);
  const [photos, setPhotos]       = useState(null);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState('');
  const [notice, setNotice]       = useState('');

  // Kamera
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream]             = useState(null);
  const videoRef   = useRef(null);
  const canvasRef  = useRef(null);
  const galleryRef = useRef(null);

  const stopStream = useCallback((s) => {
    if (s) s.getTracks().forEach((t) => t.stop());
  }, []);
  useEffect(() => () => stopStream(stream), [stream, stopStream]);

  async function openCamera() {
    setError('');
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
        audio: false,
      });
      setStream(mediaStream);
      setCameraActive(true);
      requestAnimationFrame(() => {
        if (videoRef.current) videoRef.current.srcObject = mediaStream;
      });
    } catch (err) {
      setError(`Kamera tidak dapat dibuka: ${err.message}`);
    }
  }

  function closeCamera() {
    stopStream(stream);
    setStream(null);
    setCameraActive(false);
  }

  function capturePhoto() {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return setError('Gagal mengambil foto dari kamera.');
      const file = new File([blob], `kamera_${Date.now()}.jpg`, { type: 'image/jpeg' });
      addFiles([file]);
      closeCamera();
    }, 'image/jpeg', 0.92);
  }

  async function switchToView() {
    setTab('view');
    if (photos !== null) return;
    try {
      setPhotos(await loadVisitPhotos(activeVisitId));
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  function addFiles(picked) {
    const valid = picked.filter((f) => f.type.startsWith('image/'));
    if (!valid.length) return setError('Hanya file gambar (JPG, PNG, WEBP, HEIC).');
    setError('');
    setFiles((prev) => [...prev, ...valid].slice(0, 5));
    setPreviews((prev) => [...prev, ...valid.map((f) => URL.createObjectURL(f))].slice(0, 5));
  }

  function removeFile(i) {
    setFiles((prev) => prev.filter((_, j) => j !== i));
    setPreviews((prev) => prev.filter((_, j) => j !== i));
  }

  async function upload() {
    if (!activeVisitId) return setError('Data kunjungan tidak lengkap untuk upload foto.');
    if (!files.length) return setError('Pilih atau ambil minimal satu foto.');
    setError('');
    setBusy(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const compressed = await compressImage(file);
        const path = `${CLINIC_ID}/${activeVisitId}/${Date.now()}_${i}_${photoType}.jpg`;
        const publicUrl = await uploadImageFile(compressed, path);
        await addVisitPhoto(activeVisitId, {
          photo_type:   photoType,
          storage_url:  publicUrl,
          notes:        notes.trim() || null,
        });
      }
      setNotice(`${files.length} foto berhasil diunggah.`);
      setFiles([]);
      setPreviews([]);
      setNotes('');
      setPhotos(null);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && !cameraActive && onClose()}>
      <section className="modal-card" role="dialog" aria-modal="true" aria-label="Foto Kunjungan">
        <header className="modal-head">
          <div className="modal-title-icon"><Camera size={23} /></div>
          <div className="grow">
            <h2>Foto Kunjungan</h2>
            <p>{visit?.visit_number || activeVisitId || 'Kunjungan'} · {client?.name || '-'}</p>
          </div>
          <button className="icon-button" onClick={cameraActive ? closeCamera : onClose} aria-label="Tutup"><X size={20} /></button>
        </header>

        {/* Live kamera view */}
        {cameraActive ? (
          <div className="camera-view">
            <video ref={videoRef} autoPlay playsInline muted className="camera-video" />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className="camera-controls">
              <button type="button" className="button button-quiet" onClick={closeCamera}>Batal</button>
              <button type="button" className="camera-shutter" onClick={capturePhoto} aria-label="Ambil foto">
                <span className="camera-shutter-inner" />
              </button>
              <div style={{ width: 80 }} />
            </div>
          </div>
        ) : (
          <>
            <div className="photo-tab-bar">
              <button className={`photo-tab ${tab === 'upload' ? 'photo-tab-active' : ''}`}
                onClick={() => setTab('upload')}>
                <Upload size={15} /> Upload
              </button>
              <button className={`photo-tab ${tab === 'view' ? 'photo-tab-active' : ''}`}
                onClick={switchToView}>
                <Image size={15} /> Lihat Foto
              </button>
            </div>

            <div className="modal-body photo-body">
              {error  ? <div className="alert alert-danger">{error}</div> : null}
              {notice ? (
                <div className="alert alert-success">
                  {notice}
                  <button className="alert-close" onClick={() => setNotice('')}>×</button>
                </div>
              ) : null}

              {tab === 'upload' ? (
                <>
                  <div className="form-field form-field-wide">
                    <span>Jenis foto</span>
                    <div className="division-selector">
                      {photoTypes.map((opt) => (
                        <label key={opt.value}
                          className={`division-option ${photoType === opt.value ? 'division-option-active' : ''}`}>
                          <input type="radio" name="photo_type" value={opt.value}
                            checked={photoType === opt.value}
                            onChange={() => setPhotoType(opt.value)} />
                          <div><strong>{opt.label}</strong></div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <input ref={galleryRef} type="file" accept="image/*" multiple
                    style={{ display: 'none' }}
                    onChange={(e) => addFiles(Array.from(e.target.files || []))} />

                  <div className="photo-pick-row">
                    <button type="button" className="button button-soft photo-pick-btn"
                      onClick={openCamera} disabled={busy}>
                      <Camera size={18} /> Ambil Foto
                    </button>
                    <button type="button" className="button button-quiet photo-pick-btn"
                      onClick={() => galleryRef.current?.click()} disabled={busy}>
                      <Image size={18} /> Pilih dari Galeri
                    </button>
                  </div>

                  {previews.length > 0 && (
                    <div className="photo-preview-grid">
                      {previews.map((src, i) => (
                        <div key={i} className="photo-preview-item">
                          <img src={src} alt={`Preview ${i + 1}`} />
                          <button className="photo-preview-remove" onClick={() => removeFile(i)}
                            aria-label="Hapus"><X size={13} /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  <label className="form-field form-field-wide">
                    <span>Catatan (opsional)</span>
                    <input value={notes} onChange={(e) => setNotes(e.target.value)}
                      maxLength={300} placeholder="Contoh: Pipi kiri, sebelum peeling" />
                  </label>
                </>
              ) : (
                <div className="photo-gallery">
                  {photos === null ? (
                    <div className="content-loading"><div className="loader" /><span>Memuat foto…</span></div>
                  ) : photos.length ? (
                    <div className="photo-grid">
                      {photos.map((photo) => {
                        const typeLabel = photoTypes.find((t) => t.value === photo.photo_type)?.label || photo.photo_type;
                        const imgUrl = photo.storage_url || photo.gdrive_url;
                        return (
                          <div key={photo.id} className="photo-card">
                            <a href={imgUrl} target="_blank" rel="noopener noreferrer">
                              {imgUrl
                                ? <img src={imgUrl} alt={typeLabel} loading="lazy" />
                                : <div className="photo-card-drive-link">
                                    <ExternalLink size={28} />
                                    <span>Buka</span>
                                  </div>
                              }
                            </a>
                            <div className="photo-card-meta">
                              <span className={`photo-type-badge photo-type-${photo.photo_type.toLowerCase()}`}>
                                {typeLabel}
                              </span>
                              {photo.notes ? <small>{photo.notes}</small> : null}
                              <small className="muted">{dateLabel(photo.created_at, { time: true })}</small>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="empty"><Image size={28} /><p>Belum ada foto untuk kunjungan ini.</p></div>
                  )}
                </div>
              )}
            </div>

            <footer className="modal-actions">
              <button className="button button-quiet" onClick={onClose} disabled={busy}>Tutup</button>
              {tab === 'upload' ? (
                <button className="button button-primary" onClick={upload}
                  disabled={busy || !files.length}>
                  <Upload size={17} />
                  {busy ? 'Mengunggah…' : `Unggah ${files.length ? `${files.length} foto` : 'Foto'}`}
                </button>
              ) : null}
            </footer>
          </>
        )}
      </section>
    </div>
  );
}

